import { html, render, nothing } from "lit-html"
import { virtual, useState, useEffect } from "haunted"

const noop = () => {}

const FieldWrapper = ({ label, field }) => {
  return html`
    <div class="field-wrapper flex py-4 flex-wrap md:flex-nowrap">
      <label class="mb-4 md:mb-0 w-full md:w-1/4 md:pr-4 font-bold">
        ${label}
      </label>
      <div class="w-full md:w-3/4">${field}</div>
    </div>
  `
}

const LinkFieldWrapper = virtual(
  ({ label, field, form = null, onChange = noop }) => {
    const [linkErr, setLinkErr] = useState(
      field
        ? field.querySelector(".field-error")?.innerText
        : form?.errors?.link
    )
    const validateLink = (value) => {
      if (value === "") {
        setLinkErr("")
        return
      }
      try {
        new URL(value)
        setLinkErr("")
      } catch {
        setLinkErr("Please enter a valid URL.")
      }
    }

    const name = "link"
    const value = field
      ? field.querySelector("input").value
      : form
      ? form.link
      : ""
    return html`
      <div class="field-wrapper flex py-4 flex-wrap md:flex-nowrap">
        <label class="mb-4 md:mb-0 w-full md:w-1/4 md:pr-4 font-bold">
          ${label}
        </label>
        <div class="w-full md:w-3/4">
          <input
            name="${name}"
            type="url"
            .value=${value}
            ?form=${!field}
            @change="${(e) => {
              const value = e.target.value
              onChange("link", value)
              validateLink(value)
            }}}"
          />
        </div>
      </div>
      ${linkErr
        ? FieldWrapper({
            name: "link-error",
            label: "",
            field: html`
              <span class="block -mt-5 text-red text-sm field-error">
                ${linkErr}
              </span>
            `,
          })
        : nothing}
    `
  }
)

const selectStateOptions = (stateOptions, selectedValue = "") =>
  Array.from(stateOptions).map(
    (option) => html`
      <option
        value="${option.value}"
        ?selected=${option.value === selectedValue}
      >
        ${option.text}
      </option>
    `
  )

const StateFieldWrapper = ({
  label,
  onChangeState = () => {},
  form = null,
  stateData = { options: [], value: "" },
}) => html`
  <div class="field-wrapper flex py-4 flex-wrap md:flex-nowrap">
    <label class="mb-4 md:mb-0 w-full md:w-1/4 md:pr-4 font-bold">
      ${label}
    </label>
    <div class="w-full md:w-3/4">
      <select
        class="input"
        name="state"
        ?form=${!!form}
        @change=${onChangeState}
      >
        ${selectStateOptions(
          stateData.options,
          form?.location?.state || stateData.value
        )}
      </select>
    </div>
  </div>
`

export const LocationFieldsWrapper = virtual(
  ({
    key = "",
    isInPerson = false,
    isRemote = false,
    fields,
    setFormFieldValue = () => {},
    stateData = {
      options: [],
      value: "",
    },
  }) => {
    const [inPerson, setInPerson] = useState(isInPerson)
    const [remote, setRemote] = useState(isRemote)

    useEffect(() => {
      setFormFieldValue("in_person", inPerson)
    }, [inPerson])

    useEffect(() => {
      setFormFieldValue("remote", remote)
    }, [remote])

    return html`
      <div class="field-wrapper flex py-8 flex-wrap md:flex-nowrap">
        <label class="mb-4 md:mb-0 w-full md:w-1/4 md:pr-4 font-bold">
          Event type
        </label>
        <div class="mr-10 flex items-center">
          <input
            type="checkbox"
            name="in_person"
            id="id${key}_in_person"
            ?form=${!!key}
            .checked=${inPerson}
            @click=${() => setInPerson(!inPerson)}
          />
          <label for="id${key}_in_person" class="ml-3">In-person event</label>
        </div>
        <div class="flex items-center">
          <input
            type="checkbox"
            name="remote"
            id="id${key}_remote"
            ?form=${!!key}
            .checked=${remote}
            @click=${() => setRemote(!remote)}
          />
          <label for="id${key}_remote" class="ml-3">Remote event</label>
        </div>
      </div>
      <div class="${remote ? "block" : "hidden"}">
        ${LinkFieldWrapper(fields.link)}
      </div>
      <div class="${inPerson ? "block" : "hidden"}">
        ${FieldWrapper(fields.location_name)}
        ${FieldWrapper(fields.street_address)} ${FieldWrapper(fields.address2)}
        ${FieldWrapper(fields.city)}
        ${StateFieldWrapper({ ...fields.state, stateData })}
        ${FieldWrapper(fields.zip_code)}
      </div>
    `
  }
)

const LocationGroup = ({ fields }) => {
  const stateSelect = fields.state?.field?.querySelector("select")
  const stateData = stateSelect
    ? {
        options: stateSelect.options,
        value: stateSelect.value,
      }
    : { options: [], value: "" }

  return html`${LocationFieldsWrapper({
      fields,
      isInPerson: fields.in_person.field.querySelector("input").checked,
      isRemote: fields.remote.field.querySelector("input").checked,
      stateData: stateData,
    })}
    <div class="pb-6 border-b-px border-gray-border"></div>`
}

export default function renderLocationGroup(el) {
  const $group = el.closest(`.form-fields-group`)
  if ($group === null) return

  // Process fields directly
  const fields = Object.fromEntries(
    [...$group.querySelectorAll(".field-wrapper")].map(($fieldWrapper) => {
      const $label = $fieldWrapper.querySelector(`label`)
      const fieldName = $label.htmlFor.match(/id_(.+)/)[1]
      const $field = $fieldWrapper.querySelector(`.field`)
      return [
        fieldName,
        { name: fieldName, label: $label.textContent.trim(), field: $field },
      ]
    })
  )

  // Render and replace directly
  const mount = document.createElement("div")
  render(LocationGroup({ fields }), mount)
  $group.replaceWith(...mount.childNodes)
}
